import * as d3 from 'd3';
import { RenderContext } from '../types';
import { getTickFormat } from '../../tooltipHelpers';
import { getLogTickValues } from '../utils/createScales';

interface AxesConfig {
    isLogScale: boolean;
    useDecimalScale: boolean;
    timeDomain: [Date, Date];
}

export function renderYAxis(ctx: RenderContext, config: AxesConfig): void {
    const { g, scales } = ctx;
    const { isLogScale, useDecimalScale } = config;

    const yDomain = scales.y.domain() as [number, number];
    const filteredLogTickValues = getLogTickValues(yDomain, useDecimalScale);

    const yAxis = isLogScale
        ? d3.axisLeft(scales.y)
            .tickValues(filteredLogTickValues)
            .tickFormat(d => useDecimalScale ? `${(d as number).toFixed(2)}` : `${(d as number).toFixed(0)}%`)
        : d3.axisLeft(scales.y)
            .ticks(5)
            .tickFormat(d => useDecimalScale ? `${(d as number).toFixed(2)}` : `${(d as number).toFixed(0)}%`);

    g.append('g')
        .call(yAxis)
        .style('pointer-events', 'none');
}

export function renderXAxis(ctx: RenderContext, config: AxesConfig): void {
    const { g, scales, layout } = ctx;
    const { timeDomain } = config;

    const tickValues = scales.x.ticks();
    const format = getTickFormat(timeDomain, tickValues);

    // X-axis position: at bottom of volume panel if shown, otherwise at bottom of price chart
    const xAxisY = layout.showVolume ? layout.volumeYOffset + layout.volumeHeight : layout.priceHeight;

    g.append('g')
        .attr('transform', `translate(0,${xAxisY})`)
        .call(d3.axisBottom(scales.x)
            .tickFormat(d => {
                const date = d as Date;
                const dateStr = date.toLocaleDateString('de-DE', { month: 'short', day: 'numeric' });

                switch (format) {
                    case 'date':
                        return dateStr;
                    case 'date-time':
                        return `${dateStr} ${d3.timeFormat('%H:%M')(date)}`;
                    case 'date-time-seconds':
                        return `${dateStr} ${d3.timeFormat('%H:%M:%S')(date)}`;
                    case 'time':
                        return d3.timeFormat('%H:%M')(date);
                    case 'time-seconds':
                        return d3.timeFormat('%H:%M:%S')(date);
                    default:
                        return dateStr;
                }
            }))
        .style('pointer-events', 'none');
}

export function renderEmptyAxes(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    innerWidth: number,
    height: number,
    useDecimalScale: boolean,
    theme: any
): void {
    // Create Y scale for empty state
    const y = d3.scaleLinear()
        .domain(useDecimalScale ? [0, 1] : [0, 100])
        .range([height, 0])
        .nice();

    const yAxis = d3.axisLeft(y)
        .ticks(5)
        .tickFormat(d => useDecimalScale ? `${d}` : `${d}%`);

    g.append('g')
        .call(yAxis)
        .style('pointer-events', 'none');

    // Add X axis line (no labels)
    g.append('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', height)
        .attr('y2', height)
        .attr('stroke', theme.palette.divider)
        .attr('stroke-width', 1);
}

export function renderVolumeYAxis(ctx: RenderContext): void {
    const { g, scales, layout, theme } = ctx;

    if (!scales.yVolume || !layout.showVolume) return;

    // Create volume group positioned below the price chart
    const volumeAxisGroup = g.append('g')
        .attr('class', 'volume-y-axis')
        .attr('transform', `translate(0, ${layout.volumeYOffset})`);

    // Volume Y axis (left side, minimal ticks)
    const yVolumeAxis = d3.axisLeft(scales.yVolume)
        .ticks(3)
        .tickFormat(d => formatVolume(d as number));

    volumeAxisGroup.append('g')
        .call(yVolumeAxis)
        .style('pointer-events', 'none');

    // Horizontal grid lines for volume panel (subtle)
    volumeAxisGroup.selectAll('.volume-grid-line')
        .data(scales.yVolume.ticks(3))
        .join('line')
        .attr('class', 'volume-grid-line')
        .attr('x1', 0)
        .attr('x2', layout.innerWidth)
        .attr('y1', d => scales.yVolume!(d))
        .attr('y2', d => scales.yVolume!(d))
        .attr('stroke', theme.palette.divider)
        .attr('stroke-width', 0.5)
        .attr('stroke-dasharray', '2 2')
        .attr('stroke-opacity', 0.15)
        .style('pointer-events', 'none');
}

function formatVolume(value: number): string {
    if (value >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(1)}M`;
    } else if (value >= 1_000) {
        return `${(value / 1_000).toFixed(1)}K`;
    }
    return value.toFixed(0);
}
